import {
  CdkDragDrop,
  CdkDragEnd,
  CdkDragStart,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { FolderNode } from '../../models/folder-node.models';
import { FolderTreeService } from '../../services/folder-tree.service';

@Component({
  selector: 'qt-folder-structure-builder',
  templateUrl: './folder-structure-builder.component.html',
  styleUrls: ['./folder-structure-builder.component.scss'],
  imports: [
    DragDropModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslationCoreModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FolderStructureBuilderComponent
  implements OnInit, OnChanges, AfterViewInit
{
  private folderTreeService = inject(FolderTreeService);

  @Input() initialStructure: FolderNode[] = [];
  @Input() readonly = false;
  @Output() structureChanged = new EventEmitter<FolderNode[]>();

  @ViewChild('folderTreeContainer', { static: false })
  folderTreeContainer!: ElementRef<HTMLElement>;

  private folderNodesSignal = signal<FolderNode[]>([]);

  private focusNodeIdSignal = signal<string | null>(null);

  visibleNodes = computed(() => {
    const nodes = this.folderNodesSignal();
    return this.folderTreeService.getVisibleNodes(nodes);
  });

  get folderNodes(): FolderNode[] {
    return this.folderNodesSignal();
  }

  set folderNodes(value: FolderNode[]) {
    this.folderNodesSignal.set(value);
  }

  dropTargetIds: string[] = ['main-list'];

  currentDraggedNode: FolderNode | null = null;

  readonly MAX_FOLDER_LEVEL = 9;

  constructor() {
    effect(() => {
      const nodeId = this.focusNodeIdSignal();
      if (nodeId && this.folderTreeContainer) {
        requestAnimationFrame(() => {
          this.focusInputForNode(nodeId);
          this.focusNodeIdSignal.set(null);
        });
      }
    });
  }

  ngOnInit(): void {
    this.initializeStructure();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['initialStructure'] &&
      !changes['initialStructure'].firstChange
    ) {
      this.initializeStructure();
    }
  }

  ngAfterViewInit(): void {
    const pendingNodeId = this.focusNodeIdSignal();
    if (pendingNodeId) {
      this.focusInputForNode(pendingNodeId);
      this.focusNodeIdSignal.set(null);
    }
  }

  onDragStarted(event: CdkDragStart<FolderNode>): void {
    this.currentDraggedNode = event.source.data;
    const dragElement = event.source.element.nativeElement;
    dragElement.style.opacity = '0.5';
  }

  onDragEnded(event: CdkDragEnd<FolderNode>): void {
    this.currentDraggedNode = null;
    const dragElement = event.source.element.nativeElement;
    dragElement.style.opacity = '1';
  }

  drop(event: CdkDragDrop<FolderNode[]>): void {
    const draggedNode = event.item.data as FolderNode;
    const targetContainerId = event.container.id;

    if (
      targetContainerId !== 'main-list' &&
      !this.isValidDropTarget(targetContainerId)
    ) {
      return;
    }

    let hasActualChange = false;

    if (event.previousContainer === event.container) {
      if (event.previousIndex !== event.currentIndex) {
        this.moveNodeWithChildren(
          draggedNode.id,
          event.previousIndex,
          event.currentIndex
        );
        hasActualChange = true;
      }
    } else {
      if (targetContainerId === 'main-list') {
        this.moveNodeToNewParent(draggedNode.id, null);
        hasActualChange = true;
      } else {
        if (this.wouldCreateCycle(draggedNode.id, targetContainerId)) {
          return;
        }

        const targetNode = this.folderNodes.find(
          n => n.id === targetContainerId
        );
        if (targetNode) {
          const targetLevel = targetNode.level || 0;
          const draggedNodeDepth = this.calculateNodeSubtreeDepth(
            draggedNode.id
          );
          if (targetLevel + 1 + draggedNodeDepth > this.MAX_FOLDER_LEVEL) {
            return;
          }
        }

        this.moveNodeToNewParent(draggedNode.id, targetContainerId);
        hasActualChange = true;
      }
    }

    if (hasActualChange) {
      this.rebuildFolderTree();
    }
  }

  isValidDropTarget(targetNodeId: string): boolean {
    if (!this.currentDraggedNode) {
      return true;
    }

    if (targetNodeId === 'main-list') {
      return true;
    }

    if (this.wouldCreateCycle(this.currentDraggedNode.id, targetNodeId)) {
      return false;
    }

    const targetNode = this.folderNodes.find(n => n.id === targetNodeId);
    if (targetNode) {
      const targetLevel = targetNode.level || 0;
      const draggedNodeDepth = this.calculateNodeSubtreeDepth(
        this.currentDraggedNode.id
      );
      const resultingMaxLevel = targetLevel + 1 + draggedNodeDepth;
      return resultingMaxLevel <= this.MAX_FOLDER_LEVEL;
    }

    return true;
  }

  addRootFolder(): void {
    const result = this.folderTreeService.addRootFolder(this.folderNodes);
    this.updateNodesAfterOperation(result);
  }

  addRootDocumentPlaceholder(): void {
    const result = this.folderTreeService.addRootDocumentPlaceholder(
      this.folderNodes
    );
    this.updateNodesAfterOperation(result);
  }

  addDocumentPlaceholder(parent: FolderNode): void {
    const result = this.folderTreeService.addDocumentPlaceholder(
      parent.id,
      this.folderNodes
    );
    this.updateNodesAfterOperation(result);
  }

  addSubFolder(parent: FolderNode): void {
    const result = this.folderTreeService.addSubFolder(
      parent.id,
      this.folderNodes
    );
    this.updateNodesAfterOperation(result);
  }

  startEdit(node: FolderNode): void {
    node.originalName = node.name;
    node.isEditing = true;

    this.scheduleInputFocus(node.id);
  }

  finishEdit(node: FolderNode): void {
    const originalName = node.originalName;
    node.isEditing = false;
    node.name = node.name.trim();

    if (!node.name) {
      node.name = 'Untitled Folder';
    }

    const maxLength = 150;
    if (node.name.length > maxLength) {
      node.name = node.name.substring(0, maxLength).trim();
    }

    if (node.name !== originalName) {
      this.emitStructureChange();
    }

    delete node.originalName;
  }

  deleteFolder(node: FolderNode): void {
    this.folderNodes = this.folderTreeService.deleteFolder(
      node.id,
      this.folderNodes
    );
    this.emitStructureChange();
  }

  toggleExpanded(node: FolderNode): void {
    this.folderNodes = this.folderTreeService.toggleExpanded(
      node.id,
      this.folderNodes
    );
  }

  getFolderIcon(node: FolderNode): string {
    return this.folderTreeService.getFolderIcon(node);
  }

  getExpandIcon(node: FolderNode): string {
    return this.folderTreeService.getExpandIcon(node);
  }

  isDocumentPlaceholder(node: FolderNode): boolean {
    return this.folderTreeService.isDocumentPlaceholder(node);
  }

  isAtMaxLevel(node: FolderNode): boolean {
    return (node.level || 0) >= this.MAX_FOLDER_LEVEL;
  }

  canAddChildren(node: FolderNode): boolean {
    return !this.isAtMaxLevel(node) && !this.isDocumentPlaceholder(node);
  }

  private moveNodeWithChildren(
    nodeId: string,
    fromIndex: number,
    toIndex: number
  ): void {
    this.folderNodes = this.folderTreeService.moveNodeWithChildren(
      nodeId,
      fromIndex,
      toIndex,
      this.folderNodes
    );
  }

  private wouldCreateCycle(
    draggedNodeId: string,
    targetNodeId: string
  ): boolean {
    return this.folderTreeService.wouldCreateCycle(
      draggedNodeId,
      targetNodeId,
      this.folderNodes
    );
  }

  private moveNodeToNewParent(
    nodeId: string,
    newParentId: string | null
  ): void {
    this.folderNodes = this.folderTreeService.moveNodeToNewParent(
      nodeId,
      newParentId,
      this.folderNodes
    );
  }

  private rebuildFolderTree(): void {
    this.folderNodes = this.folderTreeService.rebuildFolderTree(
      this.folderNodes
    );
    this.updateDropTargetIds();
    this.emitStructureChange();
  }

  private emitStructureChange(): void {
    this.structureChanged.emit([...this.folderNodes]);
  }

  private updateNodesAfterOperation(result: {
    nodes: FolderNode[];
    newNodeId: string;
  }): void {
    this.folderNodes = result.nodes;
    this.updateDropTargetIds();
    this.emitStructureChange();
    this.scheduleInputFocus(result.newNodeId);
  }

  private initializeStructure(): void {
    this.folderNodes = this.folderTreeService.initializeStructure(
      this.initialStructure
    );
    this.updateDropTargetIds();
  }

  private focusInputForNode(nodeId: string): void {
    if (!this.folderTreeContainer) {
      return;
    }

    const input = this.folderTreeContainer.nativeElement.querySelector(
      `input[data-folder-id="${nodeId}"]`
    ) as HTMLInputElement;

    if (input) {
      input.focus();
      input.select();
    }
  }

  private scheduleInputFocus(nodeId: string): void {
    this.focusNodeIdSignal.set(nodeId);
  }

  private updateDropTargetIds(): void {
    this.dropTargetIds = this.folderTreeService.generateDropTargetIds(
      this.folderNodes
    );
  }

  private calculateNodeSubtreeDepth(nodeId: string): number {
    const children = this.folderNodes.filter(n => n.pid === nodeId);

    if (children.length === 0) {
      return 0;
    }

    let maxChildDepth = 0;
    for (const child of children) {
      const childDepth = this.calculateNodeSubtreeDepth(child.id);
      maxChildDepth = Math.max(maxChildDepth, childDepth);
    }

    return maxChildDepth + 1;
  }
}
